import { PenIcon } from "@xola/icons";
import { Label } from "@xola/ui-kit";
import clsx from "clsx";
import { SwitchField } from "common";
import React, { useState } from "react";
import { LineItemSelectorType, LineItemTemplate, SelectionState } from "types";
import { useResource } from "../../hooks/useResource";
import { LineItemTemplateImage } from "./LineItemTemplateImage";
import { LineItemSelectorModal } from "./LineItemTemplateSelectorModal";
import { SecondaryButton } from "./SecondaryButton";
import { isDefaultDemographic } from "@/models/DemographicUnit";

interface SelectedLineItemTemplateListInputProps {
    label: string;
    value: SelectionState;
    onChange: (value: SelectionState, secondaryValue?: SelectionState) => void;
    type: LineItemSelectorType; // TODO: Add more types
    isSelectAll?: boolean;
    error?: string | null;
    allowedIds?: ID[];
    /** Adds a second section to the picker so one rule can span two unit types. */
    secondaryType?: LineItemSelectorType;
    secondaryValue?: SelectionState;
    secondaryAllowedIds?: ID[];
    sectionLabels?: { primary: string; secondary: string };
}

export const SelectedLineItemTemplateListInput = ({
    label,
    value,
    onChange,
    type,
    isSelectAll,
    error,
    allowedIds,
    secondaryType,
    secondaryValue,
    secondaryAllowedIds,
    sectionLabels,
}: SelectedLineItemTemplateListInputProps) => {
    const [isModalOpen, setIsModalOpen] = useState(false);

    return (
        <div>
            <LineItemSelectorModal
                isOpen={isModalOpen}
                title={label}
                type={type}
                allowedIds={allowedIds}
                value={value}
                secondaryType={secondaryType}
                secondaryValue={secondaryValue}
                secondaryAllowedIds={secondaryAllowedIds}
                sectionLabels={sectionLabels}
                onChange={onChange}
                onClose={() => {
                    setIsModalOpen(false);
                }}
            />

            <div
                className="mb-2 flex items-end justify-between"
                data-testid={`selected-line-item-template-list-input-${type}`}
            >
                <div>
                    <Label className={clsx("whitespace-nowrap", error && "!text-danger")}>
                        {label} {error}
                    </Label>
                </div>

                <div>
                    {isSelectAll ? (
                        <SwitchField
                            size="small"
                            isChecked={value.all}
                            onChange={(isChecked) => {
                                onChange({ all: isChecked, selected: [] });
                            }}
                        >
                            Select All
                        </SwitchField>
                    ) : null}

                    <SecondaryButton
                        className="ml-4"
                        data-testid="selected-line-item-template-list-input-button"
                        size="small"
                        disabled={isSelectAll ? value.all : false}
                        onClick={() => {
                            setIsModalOpen(true);
                        }}
                    >
                        <PenIcon data-testid={`line-item-template-list-input-${type}-input`} />
                    </SecondaryButton>
                </div>
            </div>

            <div className="max-h-40 divide-y divide-gray-lighter overflow-y-auto rounded-lg border border-gray-lighter">
                {value.selected.length > 0 || (secondaryValue?.selected.length ?? 0) > 0 ? (
                    <>
                        {value.selected.map((id) => (
                            <LineItemTemplateItem key={id} id={id} type={type} />
                        ))}
                        {secondaryType
                            ? secondaryValue?.selected.map((id) => (
                                  <LineItemTemplateItem key={id} id={id} type={secondaryType} />
                              ))
                            : null}
                    </>
                ) : isSelectAll ? null : (
                    <div className="py-3 px-4 text-gray-dark">
                        {value.all || secondaryValue?.all ? "All items selected" : "No item selected"}
                    </div>
                )}
            </div>
        </div>
    );
};

const LineItemTemplateItem = ({ id, type }: { id: ID; type: LineItemSelectorType }) => {
    const { data: lineItemTemplate } = useResource<LineItemTemplate>(["lineItemTemplates", id]);
    const shouldTranslate = isDefaultDemographic(lineItemTemplate?.name);

    return (
        <span className="flex items-center py-2 px-3">
            <LineItemTemplateImage lineItemTemplate={lineItemTemplate} type={type} size="tiny" />
            <span className={clsx("ml-2 font-bold text-black", !shouldTranslate && "no-translate")}>
                {lineItemTemplate?.name}
            </span>
        </span>
    );
};
